import { useState, useEffect, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { supabase } from '@/integrations/supabase/client';
import { LogOut, Mail, Settings, User } from 'lucide-react';
import PasswordChangeDialog from '@/components/PasswordChangeDialog';
import MessageStats from '@/components/admin/MessageStats';
import MessageFilters from '@/components/admin/MessageFilters';
import MessageCard from '@/components/admin/MessageCard';
import MessageAnalytics from '@/components/admin/MessageAnalytics';
import { Card } from '@/components/ui/card';

interface ContactMessage {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  subject: string;
  message: string;
  service_type: string;
  created_at: string;
  read_at: string | null;
}

const AdminDashboard = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  
  const { user, token, logout, isLoading: authLoading } = useAdminAuth();
  const { toast } = useToast();

  const fetchMessages = async () => {
    if (!token) return;

    try {
      const { data, error } = await supabase.functions.invoke('contact-messages?action=list', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (error) throw error;

      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load messages',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchMessages();
  };

  const markAsRead = async (messageId: string) => {
    if (!token) return;

    try {
      await supabase.functions.invoke('contact-messages?action=mark-read', {
        method: 'POST',
        body: { messageId },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, read_at: new Date().toISOString() }
            : msg
        )
      );

      toast({
        title: 'Success',
        description: 'Message marked as read'
      });
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to update message',
        variant: 'destructive'
      });
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!token) return;

    try {
      await supabase.functions.invoke('contact-messages?action=delete', {
        method: 'POST',
        body: { messageId },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setMessages(prev => prev.filter(msg => msg.id !== messageId));

      toast({
        title: 'Success',
        description: 'Message deleted'
      });
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete message',
        variant: 'destructive'
      });
    }
  };

  const exportToCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Subject', 'Message', 'Service Type', 'Date', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredMessages.map(msg => [
        `"${msg.first_name}"`,
        `"${msg.last_name}"`,
        `"${msg.email}"`,
        `"${msg.subject.replace(/"/g, '""')}"`,
        `"${msg.message.replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        `"${msg.service_type || ''}"`,
        `"${formatDate(msg.created_at)}"`,
        `"${msg.read_at ? 'Read' : 'Unread'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `messages_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast({
      title: 'Exported',
      description: `${filteredMessages.length} messages exported to CSV`
    });
  };

  useEffect(() => {
    if (token) {
      fetchMessages();
    }
  }, [token]);

  const handleLogout = () => {
    logout();
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtered messages
  const filteredMessages = useMemo(() => {
    return messages.filter(msg => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        msg.first_name.toLowerCase().includes(searchLower) ||
        msg.last_name.toLowerCase().includes(searchLower) ||
        msg.email.toLowerCase().includes(searchLower) ||
        msg.subject.toLowerCase().includes(searchLower) ||
        msg.message.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'unread' && !msg.read_at) ||
        (statusFilter === 'read' && msg.read_at);

      // Service filter
      const matchesService = serviceFilter === 'all' || msg.service_type === serviceFilter;

      return matchesSearch && matchesStatus && matchesService;
    });
  }, [messages, searchTerm, statusFilter, serviceFilter]);

  // Stats
  const unreadCount = messages.filter(msg => !msg.read_at).length;
  const todayCount = messages.filter(msg => {
    const today = new Date().toISOString().split('T')[0];
    return msg.created_at.startsWith(today);
  }).length;

  // Handle redirects and loading states after all hooks
  if (!authLoading && !user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/10 bg-background/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-glow-cyber">
                Belada.at
              </Link>
              <Badge variant="secondary" className="bg-gradient-cyber text-primary-foreground">
                Admin Panel
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                {user?.username}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPasswordDialogOpen(true)}
                className="glass border-cyber-blue text-cyber-blue hover:bg-cyber-blue/10"
              >
                <Settings className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="glass border-destructive text-destructive hover:bg-destructive/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-glow-cyber">Contact Messages</h1>
          <p className="text-muted-foreground">
            Manage and review messages from your website visitors
          </p>
        </div>

        {/* Stats */}
        <MessageStats 
          totalMessages={messages.length}
          unreadCount={unreadCount}
          todayCount={todayCount}
        />

        {/* Analytics */}
        {messages.length > 0 && (
          <MessageAnalytics messages={messages} />
        )}

        {/* Filters */}
        <MessageFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          serviceFilter={serviceFilter}
          onServiceChange={setServiceFilter}
          onExport={exportToCSV}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />

        {/* Messages List */}
        <div className="space-y-4">
          {filteredMessages.length === 0 ? (
            <Card className="glass p-8 text-center border-glow">
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {messages.length === 0 ? 'No messages yet' : 'No messages match your filters'}
              </h3>
              <p className="text-muted-foreground">
                {messages.length === 0 
                  ? 'Contact messages will appear here when visitors submit the contact form.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </Card>
          ) : (
            filteredMessages.map((message) => (
              <MessageCard
                key={message.id}
                message={message}
                onMarkAsRead={markAsRead}
                onDelete={deleteMessage}
                formatDate={formatDate}
              />
            ))
          )}
        </div>
      </main>

      <PasswordChangeDialog 
        open={isPasswordDialogOpen} 
        onOpenChange={setIsPasswordDialogOpen} 
      />
    </div>
  );
};

export default AdminDashboard;
